import {useCallback, useEffect, useRef, useState} from "react"
import OrderApprovalPermission from "../../../components/orders/order-approval-permission"
import OrderConfirmationEntry from "../../../components/orders/order-confirmation-entry"
import OrderAmounts from "../../../components/orders/order-amounts"
import OrderProposalHistory from "../../../components/orders/order-proposal-history"
import {formatOrderMoney, singleOrderCurrency} from "../../../components/orders/order-money"
import ExcludedOrderProducts from "../../../components/orders/excluded-order-products"
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material"
import {
    CalendarToday as CalendarIcon,
    Cancel as RejectIcon,
    Cancel as XCircleIcon,
    Check as ApproveIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon,
    Info as InfoIcon,
    Inventory as InventoryIcon,
    LocalShipping as ShippingIcon,
    Person as UserIcon,
    PictureAsPdf as PdfIcon,
    Schedule as ScheduleIcon,
    TableChart as ExcelIcon,
    Warning as WarningIcon,
} from "@mui/icons-material"
import {useTranslation} from "react-i18next"
import {exportToExcel, exportToPdf} from "../../../utils/export-utils"
import {formatDateNow} from "../../../i18n/date-formatting";
import {buildShippingAddress, getOrderProducts, getTenant} from "../../../data-tables-distributor/orders/order-api"
import OrderCourierAssignment from "../../../components/orders/order-courier-assignment"
import {useUser} from "../../../providers/user-context"
import axios from "../../../config/axios-config"

// Order status configuration
const orderStatuses = {
    pending: {
        label: "Pending",
        color: "warning",
        icon: <ScheduleIcon />,
    },
    processing: {
        label: "Processing",
        color: "info",
        icon: <InfoIcon />,
    },
    shipped: {
        label: "Shipped",
        color: "primary",
        icon: <ShippingIcon />,
    },
    completed: {
        label: "Completed",
        color: "success",
        icon: <CheckCircleIcon />,
    },
    cancelled: {
        label: "Cancelled",
        color: "error",
        icon: <XCircleIcon />,
    },
}

// Priority configuration
const priorities = {
    high: { label: "High Priority", color: "error", icon: <WarningIcon /> },
    medium: { label: "Medium Priority", color: "warning", icon: <ScheduleIcon /> },
    low: { label: "Low Priority", color: "info", icon: <CheckCircleIcon /> },
}

const OrderDetailsDialogDistributor = ({
                                           open,
                                           onClose,
                                           order: providedOrder,
                                           onApprove,
                                           onQuickApprove,
                                           onOpenRevision,
                                           onReject,
                                           showActions = false,
                                           showExportInHeader = true,
                                           maxWidth = "xl",
                                           fullWidth = true,
                                       }) => {
    const { t, i18n } = useTranslation()
    const {tenantId, tenantType, userRole, userFullName, userIdentity} = useUser()
    const detailScope = [open, tenantId, tenantType, userIdentity, userRole, i18n.language,
        providedOrder?.orderId, providedOrder?.orderNumber, providedOrder?.pharmacyId].join(":")
    const [snapshot, setSnapshot] = useState({})
    const order = snapshot.scope === detailScope && snapshot.source === providedOrder ? snapshot.order : providedOrder
    const setOrder = useCallback(value => setSnapshot(previous => {
        const current = previous.scope === detailScope && previous.source === providedOrder ? previous.order : providedOrder
        // Courier reads return a new object even when the order is unchanged; keep the products' snapshot stable.
        if (current && Object.entries(value).every(([key, field]) => Object.is(current[key], field))) {
            return previous
        }
        return {scope: detailScope, source: providedOrder, order: value}
    }), [detailScope, providedOrder])
    const [isExporting, setIsExporting] = useState(false)
    const exportRequest = useRef(null)
    useEffect(() => {setIsExporting(false); return () => exportRequest.current?.abort()}, [detailScope])
    const [pharmacy, setPharmacy] = useState()
    const [productRead, setProductRead] = useState({})
    const [approvalState, setApprovalState] = useState(null)
    const [reload, setReload] = useState({})
    const [orderRead, setOrderRead] = useState({})
    const resolvedViews = useRef({})
    const refreshVersion = reload.scope === detailScope ? reload.version : 0
    const currentOrderRead = orderRead.scope === detailScope && orderRead.source === providedOrder && orderRead.version === refreshVersion ? orderRead : null
    const orderRefreshing = refreshVersion > 0 && (!currentOrderRead || currentOrderRead.loading)
    const orderRefreshFailed = Boolean(refreshVersion > 0 && currentOrderRead?.error)
    const currentProducts = productRead.scope === detailScope && productRead.order === order && productRead.version === refreshVersion ? productRead : null
    const orderProducts = currentProducts?.data || []
    // Zero confirmed quantities retain history, but do not belong in the products being fulfilled or their exports.
    const includedProducts = orderProducts.filter(product => product.productCount !== 0)
    const excludedProducts = orderProducts.filter(product => product.productCount === 0)
    const currency = singleOrderCurrency(orderProducts)
    const refreshing = orderRefreshing || !orderRefreshFailed && open && (!currentProducts || currentProducts.loading)
    const refreshFailed = orderRefreshFailed || Boolean(currentProducts?.error)
    const approvalScope = `${detailScope}:${refreshVersion}`
    const receiveApproval = useCallback(view => setApprovalState({scope: approvalScope, view}), [approvalScope])
    // Eligibility comes from the complete commercial summary, rather than one legacy row's PENDING status.
    const canQuickApprove = open && Boolean(userIdentity) && tenantType === "IMPORTER" && ["ADMIN", "OPERATOR", "SYSTEM"].includes(userRole)
        && approvalState?.scope === approvalScope && approvalState.view?.summary?.outcome === "UNREVIEWED"
        && order?.orderId === providedOrder?.orderId && order?.status === "PENDING" && !order?.courierProfileId && !refreshing && !refreshFailed

    const refreshDetails = useCallback(view => {
        // Closing a resolved popup and its next banner read can report the same revision.
        if (view?.proposalId) {
            if (resolvedViews.current.scope !== detailScope) resolvedViews.current = {scope: detailScope, keys: new Set()}
            const key = JSON.stringify([view.proposalId, view.state, Object.entries(view.current?.versions || {}).sort()])
            if (resolvedViews.current.keys.has(key)) return
            resolvedViews.current.keys.add(key)
        }
        setApprovalState(null)
        setReload(previous => ({scope: detailScope, version: previous.scope === detailScope ? previous.version + 1 : 1}))
    }, [detailScope])

    useEffect(() => {
        if (!open || !providedOrder?.pharmacyId || !refreshVersion) return
        const controller = new AbortController()
        const config = {signal: controller.signal, timeout: 10000, headers: {"Language-Context": i18n.language.toUpperCase()}}
        const read = {scope: detailScope, source: providedOrder, version: refreshVersion}
        setOrderRead({...read, loading: true})
        axios.get(`/api/v1/orders/importer/${encodeURIComponent(providedOrder.orderNumber)}`,
            {...config, params: {buyerId: providedOrder.pharmacyId}}).then(({data}) => {
                if (controller.signal.aborted) return
                setOrder(data)
                setOrderRead(read)
            }).catch(() => {
                if (!controller.signal.aborted) setOrderRead({...read, error: true})
            })
        return () => controller.abort()
    }, [open, providedOrder, detailScope, refreshVersion, i18n.language, setOrder])

    useEffect(() => {
        if (!open || !order?.pharmacyId || orderRefreshing || orderRefreshFailed) return
        const controller = new AbortController()
        const read = {scope: detailScope, order, version: refreshVersion}
        setProductRead({...read, loading: true})
        // Product rows belong to this exact snapshot. A newer courier/order read cancels and replaces them.
        getOrderProducts({order, config: {signal: controller.signal, timeout: 10000}}).then(data => {
            if (!controller.signal.aborted) setProductRead({...read, data})
        }).catch(() => {
            if (!controller.signal.aborted) setProductRead({...read, error: true})
        })
        return () => controller.abort()
    }, [open, order, detailScope, refreshVersion, i18n.language, orderRefreshing, orderRefreshFailed])

    useEffect(() => {
        if (!open || !providedOrder?.pharmacyId) return
        const controller = new AbortController()
        setPharmacy(null)
        getTenant({order: providedOrder, config: {signal: controller.signal, timeout: 10000}})
            .then(tenant => {if (!controller.signal.aborted) setPharmacy(tenant)})
        return () => controller.abort()
    }, [open, providedOrder, detailScope])

    // Handle export
    const handleExport = async (format) => {
        if (!order) return

        setIsExporting(true)
        exportRequest.current?.abort()
        const controller = new AbortController(); exportRequest.current = controller
        try {
            let fileName
            if (format === "pdf") {
                fileName = await exportToPdf(order, buildShippingAddress({pharmacy}), orderProducts, i18n.language, controller.signal)
            } else if (format === "excel") {
                fileName = await exportToExcel(order, buildShippingAddress({pharmacy}), orderProducts, i18n.language, controller.signal)
            }

            // Show success message
            console.log(`Successfully exported to ${format}: ${fileName}`)
        } catch (error) {
            if (controller.signal.aborted) {return}
            console.error(`Error exporting to ${format}:`, error)
            alert(`Failed to export to ${format}. Please try again.`)
        } finally {
            if (!controller.signal.aborted) {setIsExporting(false)}
        }
    }

    const formatDate = (dateString) => {
        return formatDateNow(dateString, i18n.language, false)
    }

    // Status Badge Component
    const StatusBadge = ({ status }) => {
        if (!status) {
            return null
        }
        const statusConfig = orderStatuses[status.toLowerCase()]
        if (!statusConfig) {
            return null
        }
        return (
            <Chip
                label={t(statusConfig.label)}
                color={statusConfig.color}
                size="small"
                icon={statusConfig.icon}
                variant="filled"
            />
        )
    }

    // Priority Chip Component
    const PriorityChip = ({ priority }) => {
        if (!priority) return null
        const priorityConfig = priorities[priority]
        if (!priorityConfig) return null
        return (
            <Chip
                label={t(priorityConfig.label)}
                color={priorityConfig.color}
                size="small"
                icon={priorityConfig.icon}
                variant="outlined"
            />
        )
    }

    if (!order) {
        return null
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth={fullWidth} id="table-to-export" PaperProps={{sx: {
            m: {xs: 1, sm: 2}, width: fullWidth ? {xs: "calc(100% - 16px)", sm: "calc(100% - 32px)"} : undefined,
            maxHeight: {xs: "calc(100% - 16px)", sm: "calc(100% - 32px)"}
        }}}>
            <DialogTitle>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <InventoryIcon color="primary" />
                        <Typography variant="h6" component="span">
                            {t("OrderDetails")} - {order.orderNumber}
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {showExportInHeader && (
                            <>
                                <Tooltip title={`${t("ExportAs")} PDF`} arrow>
                                    <span>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<PdfIcon />}
                                        onClick={() => handleExport("pdf")}
                                        disabled={isExporting || refreshing || refreshFailed}
                                    >
                                        {isExporting ? t("Exporting") : `${t("ExportAs")} PDF`}
                                    </Button>
                                    </span>
                                </Tooltip>
                                <Tooltip title={`${t("ExportAs")} Excel`} arrow>
                                    <span>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<ExcelIcon />}
                                        onClick={() => handleExport("excel")}
                                        disabled={isExporting || refreshing || refreshFailed}
                                    >
                                        {isExporting ? t("Exporting") : `${t("ExportAs")} Excel`}
                                    </Button>
                                    </span>
                                </Tooltip>
                            </>
                        )}
                        <Tooltip title={t("Close")} arrow>
                            <IconButton onClick={onClose} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
                <OrderAmounts lineId={order?.orderId} active={open} revision={refreshVersion}/>
                <OrderProposalHistory lineId={order?.orderId} active={open} revision={refreshVersion} onOpenRevision={onOpenRevision}/>
                <OrderApprovalPermission key={approvalScope} lineId={order?.orderId} active={open && !refreshing && !refreshFailed} onStateChange={receiveApproval}/>
                <OrderConfirmationEntry key={detailScope} lineId={order?.orderId} orderStatus={order?.status} active={open} onRefresh={refreshDetails}/>
                {refreshing && <Typography role="status">{t("Loading")}</Typography>}
                {refreshFailed && <Alert severity="error" action={<Button onClick={() => refreshDetails()}>{t("Retry")}</Button>}>
                    {t("DistributorOrderDetailsLoadFailed")}</Alert>}
                <Box>
                    {/* Header Information */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {/* Customer Information */}
                        <Grid item xs={12} md={4}>
                            <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
                                <CardContent>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        color="primary"
                                        sx={{
                                            borderBottom: 1,
                                            borderColor: "divider",
                                            pb: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        <UserIcon />
                                        {t("CustomerInformation")}
                                    </Typography>
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="body2" sx={{ mb: 1.5, display: "flex", justifyContent: "space-between" }}>
                                            <strong>{t("Name")}:</strong>
                                            <span>{order.pharmacyName}</span>
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1.5, display: "flex", justifyContent: "space-between" }}>
                                            <strong>{t("Email")}:</strong>
                                            <span>{order.pharmacyEmail}</span>
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1.5, display: "flex", justifyContent: "space-between" }}>
                                            <strong>{t("Address")}:</strong>
                                            <span>{buildShippingAddress({pharmacy})}</span>
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        {/* Order Information */}
                        <Grid item xs={12} md={4}>
                            <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
                                <CardContent>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        color="primary"
                                        sx={{
                                            borderBottom: 1,
                                            borderColor: "divider",
                                            pb: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        <CalendarIcon />
                                        {t("OrderInformation")}
                                    </Typography>
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="body2" sx={{ mb: 1.5, display: "flex", justifyContent: "space-between" }}>
                                            <strong>{t("OrderDate")}:</strong>
                                            <span>{formatDate(order.orderDate)}</span>
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1.5, display: "flex", justifyContent: "space-between" }}>
                                            <strong>{t("DeliveryDate")}:</strong>
                                            <span>{formatDate(order.deliveryDate || order.requestedDeliveryDate)}</span>
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1.5, display: "flex", justifyContent: "space-between" }}>
                                            <strong>{t("Payment")}:</strong>
                                            <span>{order.paymentMethod}</span>
                                        </Typography>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                                            <strong>{t("Status")}:</strong>
                                            <StatusBadge status={order.status} />
                                        </Box>
                                        {order.priority && (
                                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                <strong>{t("Priority")}:</strong>
                                                <PriorityChip priority={order.priority} />
                                            </Box>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        {/* Order Summary */}
                        <Grid item xs={12} md={4}>
                            <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
                                <CardContent>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        color="primary"
                                        sx={{
                                            borderBottom: 1,
                                            borderColor: "divider",
                                            pb: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        {t("OrderSummary")}
                                    </Typography>
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="body2" sx={{ mb: 1.5, display: "flex", justifyContent: "space-between" }}>
                                            <strong>{t("TotalItems")}:</strong>
                                            <span>{`${order.productCount} ${t("Items")}`}</span>
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1.5, display: "flex", justifyContent: "space-between" }}>
                                            <strong>{t("Subtotal")}:</strong>
                                            <span>{formatOrderMoney(order.totalPrice, currency, i18n.language)}</span>
                                        </Typography>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography
                                            variant="h6"
                                            sx={{ display: "flex", justifyContent: "space-between", color: "primary.dark" }}
                                        >
                                            <strong>{t("TotalAmount")}:</strong>
                                            <strong>{formatOrderMoney(order.totalPrice + order.shippingCost, currency, i18n.language)}</strong>
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        {/* Special Notes */}
                        {order.notes && (
                            <Grid item xs={12}>
                                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                    <CardContent>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            color="warning.dark"
                                            sx={{ display: "flex", alignItems: "center", gap: 1 }}
                                        >
                                            <WarningIcon />
                                            {t("SpecialNotes")}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontStyle: "italic", color: "warning.dark" }}>
                                            "{order.notes}"
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                    {/* Detailed Products Table */}
                    {/* Keep the courier panel mounted while its refreshed snapshot reloads products. */}
                    {!orderRefreshing && !orderRefreshFailed && <OrderCourierAssignment key={`${tenantId}:${userFullName}:${userRole}:${i18n.language}:${order.pharmacyId}:${order.orderNumber}`} order={order} open={open} onOrderRefresh={setOrder}/>}
                    {includedProducts.length > 0 && (
                        <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{ p: 2, bgcolor: "grey.50", borderBottom: 1, borderColor: "divider" }}>
                                    <Typography variant="h6" color="primary" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <InventoryIcon />
                                        {t(excludedProducts.length ? "OrderIncludedProducts" : "ProductDetailsInformation")} ({includedProducts.length} {t("Products")})
                                    </Typography>
                                </Box>
                                <TableContainer sx={{ maxHeight: 400 }}>
                                    <Table stickyHeader size="small" aria-label={t("OrderIncludedProducts")}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ bgcolor: "primary.main", color: "white", fontWeight: "bold", minWidth: 100 }}>
                                                    {t("ProductCode")}
                                                </TableCell>
                                                <TableCell sx={{ bgcolor: "primary.main", color: "white", fontWeight: "bold", minWidth: 200 }}>
                                                    {t("ProductName")}
                                                </TableCell>
                                                <TableCell sx={{ bgcolor: "primary.main", color: "white", fontWeight: "bold", minWidth: 120 }}>
                                                    {t("Manufacturer")}
                                                </TableCell>
                                                <TableCell sx={{ bgcolor: "primary.main", color: "white", fontWeight: "bold", minWidth: 100 }}>
                                                    {t("Category")}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{ bgcolor: "primary.main", color: "white", fontWeight: "bold", minWidth: 80 }}
                                                >
                                                    {t("Quantity")}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{ bgcolor: "primary.main", color: "white", fontWeight: "bold", minWidth: 100 }}
                                                >
                                                    {t("UnitPrice")}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{ bgcolor: "primary.main", color: "white", fontWeight: "bold", minWidth: 80 }}
                                                >
                                                    {t("Discount")} %
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{ bgcolor: "primary.main", color: "white", fontWeight: "bold", minWidth: 120 }}
                                                >
                                                    {t("Total")}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{ bgcolor: "primary.main", color: "white", fontWeight: "bold", minWidth: 120 }}
                                                >
                                                    {t("ExpirationDate")}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {includedProducts.map(item => (
                                                <TableRow
                                                    key={item.orderId}
                                                    hover
                                                    sx={{
                                                        "&:nth-of-type(odd)": { bgcolor: "grey.50" },
                                                        "&:hover": { bgcolor: "action.hover" },
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight="medium" color="primary">
                                                            {item.productCode}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {item.productName}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {item.manufacturer}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={t(item.productCategory)}
                                                            size="small"
                                                            variant="outlined"
                                                            color="primary"
                                                            sx={{ fontSize: "0.75rem" }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2" fontWeight="bold" color="primary">
                                                            {item.productCount.toLocaleString()}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2">{formatOrderMoney(item.unitPrice, item.currency, i18n.language)}</Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {item.discount > 0 ? (
                                                            <Chip
                                                                label={`${item.discount}%`}
                                                                size="small"
                                                                color="success"
                                                                variant="outlined"
                                                                sx={{ fontSize: "0.75rem" }}
                                                            />
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary">
                                                                {item.discount == null ? t("DistributorMetricUnavailable") : "0%"}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2" fontWeight="bold" color="success.main">
                                                            {formatOrderMoney(item.totalPrice, item.currency, i18n.language)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2" color="text.secondary">
                                                            {formatDate(item.expiryDate)}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {/* Summary Row */}
                                            <TableRow sx={{ bgcolor: "primary.light", borderTop: 2, borderColor: "primary.main" }}>
                                                <TableCell colSpan={4}>
                                                    <Typography variant="h6" fontWeight="bold" color="primary.dark">
                                                        {t("Total")}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2" fontWeight="bold" color="primary.dark">
                                                        {includedProducts.reduce((sum, item) => sum + item.productCount, 0).toLocaleString()}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="body2" color="text.secondary">
                                                        —
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2" color="success.main" fontWeight="bold">
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="h6" fontWeight="bold" color="primary.dark">
                                                        {formatOrderMoney(order.totalPrice, currency, i18n.language)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2" color="text.secondary">
                                                        {t("FinalTotal")}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    )}
                    {excludedProducts.length > 0 && !includedProducts.length && <Alert severity="info" sx={{mb: 2}}>{t("OrderNoIncludedProducts")}</Alert>}
                    <ExcludedOrderProducts key={detailScope} products={excludedProducts}
                        lines={approvalState?.scope === approvalScope ? approvalState.view?.lines : undefined}/>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, bgcolor: "grey.50", flexWrap: "wrap", gap: 1 }}>
                <Button onClick={onClose} size="large">
                    {t("Close")}
                </Button>
                {showActions && onReject && (
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<RejectIcon />}
                        onClick={() => onReject(order)}
                        size="large"
                        disabled={order.status !== "PENDING" || refreshing || refreshFailed}
                    >
                        {t("RejectOrder")}
                    </Button>
                )}
                {showActions && onApprove && (
                    <Button
                        variant="outlined"
                        startIcon={<ApproveIcon />}
                        onClick={() => onApprove(order)}
                        size="large"
                        disabled={order.status !== "PENDING" || refreshing || refreshFailed}
                    >
                        {t("ReviewWorkspaceTitle")}
                    </Button>
                )}
                {showActions && onQuickApprove && <Button variant="contained" color="success" startIcon={<ApproveIcon/>}
                    onClick={() => onQuickApprove(order)} size="large" disabled={!canQuickApprove}>
                    {t("QuickApproveAll")}
                </Button>}
            </DialogActions>
        </Dialog>
    )
}

export default OrderDetailsDialogDistributor
